import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Lightbulb, ArrowUp, Star, Plus, X, Loader2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TheoriesPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [universes, setUniverses] = useState([]);
  const [theories, setTheories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUniverse, setFilterUniverse] = useState(searchParams.get("universe_id") || "all");
  const [sortBy, setSortBy] = useState("votes");
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ universe_id: "", title: "", content: "" });

  useEffect(() => {
    const fetchUniverses = async () => {
      try {
        const res = await axios.get(`${API}/universes`);
        setUniverses(res.data);
      } catch { /* silent */ }
    };
    fetchUniverses();
  }, []);

  useEffect(() => {
    fetchTheories();
  }, [filterUniverse, sortBy]);

  const fetchTheories = async () => {
    setLoading(true);
    try {
      let url = `${API}/theories?sort=${sortBy}`;
      if (filterUniverse && filterUniverse !== "all") url += `&universe_id=${filterUniverse}`;
      const res = await axios.get(url);
      setTheories(res.data);
    } catch {
      toast.error("Failed to load theories");
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (theoryId) => {
    if (!user) {
      toast.error("Please log in to vote");
      return;
    }
    try {
      const res = await axios.post(`${API}/theories/${theoryId}/vote`);
      setTheories(prev => prev.map(t =>
        t.id === theoryId ? { ...t, votes: res.data.votes, voters: res.data.voted ? [...(t.voters || []), user.id] : (t.voters || []).filter(v => v !== user.id) } : t
      ));
    } catch {
      toast.error("Vote failed");
    }
  };

  const handleFeature = async (theoryId) => {
    try {
      const res = await axios.post(`${API}/theories/${theoryId}/feature`);
      setTheories(prev => prev.map(t =>
        t.id === theoryId ? { ...t, featured: res.data.featured } : t
      ));
      toast.success(res.data.featured ? "Theory featured!" : "Theory unfeatured");
    } catch {
      toast.error("Action failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.universe_id || !formData.title || !formData.content) {
      toast.error("All fields are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/theories`, formData);
      toast.success("Theory submitted!");
      setShowForm(false);
      setFormData({ universe_id: "", title: "", content: "" });
      fetchTheories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const getUniverseName = (id) => universes.find(u => u.id === id)?.name || "Unknown";

  const featured = theories.filter(t => t.featured);
  const regular = theories.filter(t => !t.featured);

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-base)]">
      <SEOHead title="Fan Theories" description="Submit and vote on fan theories across your favorite fictional universes." />
      <Navbar />
      <main className="flex-1 pt-20 pb-16 px-4 max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/20 bg-amber-500/5 mb-3">
              <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-mono tracking-wider text-amber-500 uppercase">Community</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit']">Fan Theories</h1>
            <p className="text-slate-400 text-sm mt-1">Share your wildest theories and vote on others</p>
          </div>
          {user && (
            <Button
              onClick={() => setShowForm(!showForm)}
              className={showForm ? "bg-slate-700" : "btn-primary"}
              data-testid="new-theory-btn"
            >
              {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {showForm ? "Cancel" : "New Theory"}
            </Button>
          )}
        </div>

        {/* Submit Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="card-surface p-6 mb-8" data-testid="theory-form">
            <h3 className="text-white font-semibold font-['Outfit'] mb-4">Submit Your Theory</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Universe</Label>
                <Select value={formData.universe_id} onValueChange={(v) => setFormData({ ...formData, universe_id: v })}>
                  <SelectTrigger className="input-dark mt-1" data-testid="theory-universe">
                    <SelectValue placeholder="Select universe" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    {universes.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Theory Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input-dark mt-1"
                  placeholder="E.g., 'Jar Jar Binks is a Sith Lord'"
                  data-testid="theory-title"
                />
              </div>
              <div>
                <Label className="text-slate-300">Your Theory</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="input-dark mt-1 min-h-[120px]"
                  placeholder="Explain your theory with evidence from the source material..."
                  data-testid="theory-content"
                />
              </div>
              <Button type="submit" disabled={submitting} className="btn-primary" data-testid="theory-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lightbulb className="w-4 h-4 mr-2" />}
                {submitting ? "Submitting..." : "Submit Theory"}
              </Button>
            </div>
          </form>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <Select value={filterUniverse} onValueChange={setFilterUniverse}>
            <SelectTrigger className="w-48 bg-[var(--bg-surface)] border-white/10 text-white text-sm" data-testid="filter-universe">
              <SelectValue placeholder="All Universes" />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-white/10">
              <SelectItem value="all">All Universes</SelectItem>
              {universes.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-1">
            {[{v: "votes", l: "Top"}, {v: "newest", l: "New"}].map((s) => (
              <button
                key={s.v}
                onClick={() => setSortBy(s.v)}
                className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
                  sortBy === s.v ? "bg-amber-500/20 text-amber-500 border border-amber-500/30" : "bg-white/5 text-slate-400 border border-white/10 hover:text-white"
                }`}
                data-testid={`sort-${s.v}`}
              >
                {s.l}
              </button>
            ))}
          </div>
        </div>

        {/* Featured */}
        {featured.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs font-mono tracking-widest uppercase text-amber-500 mb-3 flex items-center gap-2">
              <Star className="w-3 h-3" /> Featured Theories
            </h3>
            <div className="space-y-3">
              {featured.map((theory) => (
                <TheoryCard key={theory.id} theory={theory} user={user} onVote={handleVote} onFeature={handleFeature} getUniverseName={getUniverseName} />
              ))}
            </div>
          </div>
        )}

        {/* Regular */}
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" /></div>
        ) : regular.length === 0 && featured.length === 0 ? (
          <div className="text-center py-16 card-surface">
            <Lightbulb className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No theories yet. Be the first to submit one!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="theories-list">
            {regular.map((theory) => (
              <TheoryCard key={theory.id} theory={theory} user={user} onVote={handleVote} onFeature={handleFeature} getUniverseName={getUniverseName} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

const TheoryCard = ({ theory, user, onVote, onFeature, getUniverseName }) => {
  const hasVoted = user && theory.voters?.includes(user.id);
  const isAdmin = user?.role === "admin";

  return (
    <div className={`card-surface p-5 ${theory.featured ? "border-amber-500/20" : ""}`} data-testid={`theory-${theory.id}`}>
      <div className="flex gap-4">
        {/* Vote column */}
        <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
          <button
            onClick={() => onVote(theory.id)}
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
              hasVoted ? "bg-amber-500/20 text-amber-500" : "bg-white/5 text-slate-500 hover:text-amber-500 hover:bg-amber-500/10"
            }`}
            data-testid={`vote-${theory.id}`}
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          <span className={`text-sm font-semibold ${hasVoted ? "text-amber-500" : "text-slate-400"}`}>{theory.votes}</span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 bg-white/5 px-2 py-0.5 rounded">
              {getUniverseName(theory.universe_id)}
            </span>
            {theory.featured && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded flex items-center gap-1">
                <Star className="w-2.5 h-2.5" /> Featured
              </span>
            )}
          </div>
          <h4 className="text-white font-semibold font-['Outfit'] mb-1">{theory.title}</h4>
          <p className="text-sm text-slate-400 leading-relaxed">{theory.content}</p>
          <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-600">
            <span>by {theory.author_name}</span>
            <span>{new Date(theory.created_at).toLocaleDateString()}</span>
            {isAdmin && (
              <button onClick={() => onFeature(theory.id)} className="text-amber-500 hover:underline" data-testid={`feature-${theory.id}`}>
                {theory.featured ? "Unfeature" : "Feature"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TheoriesPage;
